import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import DefaultPending from '@/components/DefaultPending'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice, OrganizationContextNotice } from '@/components/patterns/info-notice'
import { StripeConnectDeferredRouteContent } from '@/components/stripe-connect/StripeConnectFallback'
import StripeConnectOnboardingManager from '@/components/stripe-connect/StripeConnectOnboardingManager'
import { useStripeConnectOrgSession } from '@/components/stripe-connect/StripeConnectOrgSessionProvider'
import { canUseStripeConnectOnboarding } from '@/components/stripe-connect/stripeConnectStatus'
import { useStripeConnectRouteReady } from '@/components/stripe-connect/useStripeConnectRouteReady'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { LoadingState } from '@/components/patterns/loading-state'
import { ConnectAccountStatus } from '@/lib/enums/ConnectAccountStatus'
import { stripeHubLink } from '@/lib/stripe-financial-routes'
import { assertNotSalesDemoOrganization } from '@/lib/services/sales-demos/sales-demos.guard'
import {
  stripeConnectQueries,
  useSeedStripeConnectTestOnboardingDataMutation,
  useStripeConnectStatus,
} from '@/lib/services/stripe-connect/stripe-connect.query'
import { getErrorMessage } from '@/lib/utils'
import {
  createFileRoute,
  notFound,
  redirect,
  useBlocker,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import { FlaskConical, Landmark } from 'lucide-react'
import { toast } from 'sonner'

const TEST_DATA_HELPER_STORAGE_PREFIX = 'onestem:stripe-connect-test-onboarding-helper:v2:'
const TEST_DATA_REPLACEMENT_CONVERGENCE_TIMEOUT_MS = 30_000

function testDataHelperStorageKey(stripeConnectStatus) {
  const connectAccount = stripeConnectStatus?.connect_account
  const accountIdentity = connectAccount?.stripe_account_id ?? connectAccount?.id

  return accountIdentity ? `${TEST_DATA_HELPER_STORAGE_PREFIX}${accountIdentity}` : null
}

function hasUsedTestDataHelper(storageKey) {
  if (!storageKey || typeof window === 'undefined') {
    return false
  }

  try {
    return window.localStorage.getItem(storageKey) === 'complete'
  } catch {
    return false
  }
}

function rememberUsedTestDataHelper(storageKey) {
  if (!storageKey || typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(storageKey, 'complete')
  } catch {
    // The local-only helper can still continue for this mount when storage is unavailable.
  }
}

function isAccountReplacementConflict(error) {
  return error?.response?.status === 409
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/stripe/onboarding')({
  component: StripeOnboardingPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('edit', 'stripe-account')) {
      throw notFound()
    }

    await assertNotSalesDemoOrganization(context.queryClient, params.orgId)

    const stripeConnectStatus = await context.queryClient.fetchQuery(
      stripeConnectQueries.status({ organizationId: params.orgId, refresh: true })
    )
    const status = stripeConnectStatus?.status

    if (
      status &&
      status !== ConnectAccountStatus.NotCreated.Value &&
      status !== ConnectAccountStatus.Incomplete.Value
    ) {
      throw redirect({
        to: '/$orgId/settings/stripe',
        params: { orgId: params.orgId },
      })
    }
  },
})

function StripeOnboardingTestDataHelper({ isPending, onSeed }) {
  return (
    <InfoNotice
      title="Local Stripe test helper"
      icon={<FlaskConical />}
      action={
        <Button variant="notice" size="xs" disabled={isPending} onClick={onSeed}>
          <FlaskConical />
          {isPending ? 'Preparing test account…' : 'Fill Stripe test data'}
        </Button>
      }
    >
      Replace this incomplete local Stripe account with a fresh test account containing
      Stripe-approved business, identity, and bank values. You still review the form and accept
      Stripe’s terms yourself.
    </InfoNotice>
  )
}

function StripeOnboardingTestDataProgressDialog({ open }) {
  if (!open) {
    return null
  }

  return (
    <Dialog open>
      <DialogContent
        size="compact"
        showCloseButton={false}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Preparing a fresh Stripe test account</DialogTitle>
          <DialogDescription>
            Keep this page open while we replace the incomplete test account and prepare refreshed
            onboarding.
          </DialogDescription>
        </DialogHeader>
        <LoadingState size="compact" surface="transparent" label="Preparing Stripe onboarding…" />
      </DialogContent>
    </Dialog>
  )
}

function StripeOnboardingEmbeddedContent({
  orgId,
  onExit,
  onSessionConflict,
  onboardingCopy,
  onTestDataSeeded,
  onReadyChange,
  isAddingTestData,
  showTestDataHelper,
}) {
  const { isReady: isEmbeddedPageReady, setReady: setEmbeddedPageReady } =
    useStripeConnectRouteReady()
  const handleReadyChange = useCallback(
    (isReady) => {
      setEmbeddedPageReady(isReady)
      onReadyChange?.(isReady)
    },
    [onReadyChange, setEmbeddedPageReady]
  )

  return (
    <StripeConnectDeferredRouteContent isReady={isEmbeddedPageReady}>
      <div className="space-y-6">
        <AdminPageHeader icon={Landmark} title="Stripe Onboarding" />

        {showTestDataHelper ? (
          <StripeOnboardingTestDataHelper isPending={isAddingTestData} onSeed={onTestDataSeeded} />
        ) : null}

        <StripeConnectOnboardingManager
          organizationId={orgId}
          onExit={onExit}
          onReadyChange={handleReadyChange}
          onSessionConflict={onSessionConflict}
          title={onboardingCopy.sectionTitle}
          description={onboardingCopy.sectionDescription}
        />
      </div>
    </StripeConnectDeferredRouteContent>
  )
}

function StripeOnboardingContent({ orgId }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const resetConnectInstance = useStripeConnectOrgSession()?.resetConnectInstance
  const [sessionStatusOverride, setSessionStatusOverride] = useState(null)
  const [hasReplacedWithTestData, setHasReplacedWithTestData] = useState(false)
  const [isAddingTestData, setIsAddingTestData] = useState(false)
  const [onboardingGeneration, setOnboardingGeneration] = useState(0)
  const hasRefreshedAfterProvisioningRef = useRef(false)
  const { data: stripeConnectStatus } = useStripeConnectStatus({
    refresh: true,
    refetchOnMount: false,
  })
  const seedTestDataMutation = useSeedStripeConnectTestOnboardingDataMutation({
    organizationId: orgId,
  })
  const effectiveStripeStatus = sessionStatusOverride ?? stripeConnectStatus
  const status = effectiveStripeStatus?.status
  const currentStripeAccountId = effectiveStripeStatus?.connect_account?.stripe_account_id
  const testDataHelperKey = testDataHelperStorageKey(effectiveStripeStatus)
  const hasPersistedTestDataUse = hasUsedTestDataHelper(testDataHelperKey)
  const onboardingCopy = useMemo(() => {
    if (status === ConnectAccountStatus.NotCreated.Value) {
      return {
        sectionTitle: 'Start Stripe setup',
        sectionDescription:
          'Add the Stripe details required before you can accept payments and payouts.',
      }
    }

    if (status === ConnectAccountStatus.Incomplete.Value) {
      return {
        sectionTitle: 'Resume Stripe setup',
        sectionDescription:
          'Pick up where you left off so Stripe can finish enabling payments and payouts.',
      }
    }

    return {
      sectionTitle: 'Stripe onboarding',
      sectionDescription: 'Review the Stripe steps required to accept payments and payouts.',
    }
  }, [status])

  const handleSessionConflict = useCallback((nextStatus) => {
    setSessionStatusOverride(nextStatus)
  }, [])

  const handleOnboardingExit = useCallback(async () => {
    if (!orgId) {
      return
    }

    const stripeConnectStatus = await queryClient.fetchQuery(
      stripeConnectQueries.status({ organizationId: orgId, refresh: true })
    )

    setSessionStatusOverride(stripeConnectStatus)
  }, [orgId, queryClient])

  const handleOnboardingReadyChange = useCallback(
    (isReady) => {
      if (
        !isReady ||
        status !== ConnectAccountStatus.NotCreated.Value ||
        hasRefreshedAfterProvisioningRef.current
      ) {
        return
      }

      hasRefreshedAfterProvisioningRef.current = true
      void queryClient
        .fetchQuery(stripeConnectQueries.status({ organizationId: orgId, refresh: true }))
        .then(setSessionStatusOverride)
        .catch(() => {
          // The embedded onboarding component remains usable if this optional helper refresh fails.
        })
    },
    [orgId, queryClient, status]
  )

  const activateReplacementAccount = useCallback(
    ({ accountId, nextStatus = null }) => {
      rememberUsedTestDataHelper(
        accountId ? `${TEST_DATA_HELPER_STORAGE_PREFIX}${accountId}` : null
      )
      resetConnectInstance?.()
      setSessionStatusOverride(nextStatus)
      setHasReplacedWithTestData(true)
      setOnboardingGeneration((generation) => generation + 1)
    },
    [resetConnectInstance]
  )

  const handleAddTestData = useCallback(async () => {
    setIsAddingTestData(true)

    try {
      const response = await seedTestDataMutation.mutateAsync()
      activateReplacementAccount({ accountId: response?.account_id })

      try {
        const refreshedStatus = await queryClient.fetchQuery(
          stripeConnectQueries.status({ organizationId: orgId, refresh: true })
        )
        setSessionStatusOverride(refreshedStatus)
      } catch {
        // The new Account Session still resolves the active replacement when this optional status
        // refresh fails. Keep onboarding mounted and let its normal session request continue.
      }

      toast.success(response?.message ?? 'A fresh Stripe test account is ready.')
    } catch (error) {
      if (isAccountReplacementConflict(error)) {
        const deadline = Date.now() + TEST_DATA_REPLACEMENT_CONVERGENCE_TIMEOUT_MS
        let retryDelay = 0

        while (Date.now() < deadline) {
          if (retryDelay > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, retryDelay))
          }

          try {
            const refreshedStatus = await queryClient.fetchQuery({
              ...stripeConnectQueries.status({ organizationId: orgId }),
              staleTime: 0,
            })
            const refreshedStripeAccountId = refreshedStatus?.connect_account?.stripe_account_id

            if (refreshedStripeAccountId && refreshedStripeAccountId !== currentStripeAccountId) {
              activateReplacementAccount({
                accountId: refreshedStripeAccountId,
                nextStatus: refreshedStatus,
              })
              toast.success('A fresh Stripe test account is ready in another tab.')

              return
            }
          } catch {
            // A later local status read can still converge once the winning tab finishes.
          }

          retryDelay = retryDelay === 0 ? 100 : Math.min(retryDelay * 2, 2_000)
        }
      }

      toast.error('Could not add Stripe test data', {
        description: getErrorMessage(error),
      })
    } finally {
      setIsAddingTestData(false)
    }
  }, [activateReplacementAccount, currentStripeAccountId, orgId, queryClient, seedTestDataMutation])

  useBlocker({
    shouldBlockFn: () => isAddingTestData,
    enableBeforeUnload: isAddingTestData,
  })

  useEffect(() => {
    if (!orgId || !status || canUseStripeConnectOnboarding(status)) {
      return
    }

    void navigate({
      ...stripeHubLink(orgId),
      replace: true,
    })
  }, [navigate, orgId, status])

  return (
    <>
      {canUseStripeConnectOnboarding(status) ? (
        <StripeOnboardingEmbeddedContent
          key={`${orgId}:${onboardingGeneration}`}
          orgId={orgId}
          onExit={handleOnboardingExit}
          onSessionConflict={handleSessionConflict}
          onboardingCopy={onboardingCopy}
          onTestDataSeeded={handleAddTestData}
          onReadyChange={handleOnboardingReadyChange}
          isAddingTestData={isAddingTestData}
          showTestDataHelper={
            import.meta.env.DEV &&
            !hasReplacedWithTestData &&
            !hasPersistedTestDataUse &&
            effectiveStripeStatus?.can_seed_test_onboarding_data === true
          }
        />
      ) : (
        <div className="py-12">
          <DefaultPending className="h-auto min-h-[160px]" />
        </div>
      )}
      <StripeOnboardingTestDataProgressDialog open={isAddingTestData} />
    </>
  )
}

export function StripeOnboardingPage() {
  const { orgId } = useParams({ strict: false })

  return (
    <IfGlobalOrg
      show={false}
      fallback={
        <OrganizationContextNotice title="Choose an organization first">
          Stripe onboarding is organization-specific. Manage Stripe setup inside the right florist
          account.
        </OrganizationContextNotice>
      }
    >
      <StripeOnboardingContent key={orgId ?? 'unknown-org'} orgId={orgId} />
    </IfGlobalOrg>
  )
}
